import React, { useRef, useState } from "react";
import { useT } from "../../i18n/useT";
import MmtImportReview from "./MmtImportReview";
import {
  extractPeopleFromFile,
  ACCEPTED_FILE_TYPES,
} from "./importExtractionApi";
import { SAMPLE_DOC_FOR } from "./importModel";
import { SAMPLE_DOCUMENTS, SAMPLE_PDF_MIME } from "./importSampleDocuments";
import "./import.scss";

/**
 * Import flow: file entry -> reading -> Review.
 *
 * Opened from the User List as a modal, the same pattern Add and Edit use.
 *
 * The chosen file's actual bytes are sent for extraction. v100 read only
 * `file.name` and substituted a fixture; that is gone. If extraction fails the
 * flow reports the failure — it never falls back to sample people.
 */
export default function MmtImportFlow({
  session,
  customerType,
  personLabels,
  // `market` is optional: when omitted the shared regional resolver answers for
  // the runtime active region. It is never derived from the UI locale.
  market,
  isExternalIdTaken,
  onClose,
  onImported,
}) {
  const t = useT("import");
  const [stage, setStage] = useState("entry"); // entry | reading | review | error
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState(null);
  const [extractError, setExtractError] = useState("");
  const inputRef = useRef(null);

  const sampleDoc = SAMPLE_DOCUMENTS[SAMPLE_DOC_FOR[customerType] || "client"];

  const onFileChosen = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setExtractError("");
    setStage("reading");
    try {
      // The File object itself goes to the server. Nothing here inspects or
      // invents its contents, and nothing substitutes sample data.
      const people = await extractPeopleFromFile(session, file, customerType);
      setRows(people);
      setStage("review");
    } catch (err) {
      setExtractError(err?.response?.data?.message || t("extract_failed"));
      setStage("error");
    }
  };

  const reset = () => {
    setRows(null);
    setExtractError("");
    setFileName("");
    setStage("entry");
  };

  if (stage === "review" && rows) {
    return (
      <MmtImportReview
        session={session}
        rows={rows}
        personLabels={personLabels}
        market={market}
        isExternalIdTaken={isExternalIdTaken}
        onImport={onImported}
        onCancel={onClose}
      />
    );
  }

  return (
    <div className="mmt-imp">
      <div className="mmt-imp-header">
        <h1>{t("title", { people: personLabels.plural })}</h1>
        <button type="button" className="mmt-imp-close" onClick={onClose}>
          {t("cancel")}
        </button>
      </div>

      {stage === "error" ? (
        <div className="mmt-imp-entry" data-imp-stage="error">
          <p className="mmt-imp-issue">{extractError}</p>
          <button type="button" className="mmt-imp-primary" onClick={reset}>
            {t("choose_file")}
          </button>
        </div>
      ) : stage === "reading" ? (
        <div
          className="mmt-imp-reading"
          data-imp-stage="reading"
          role="status"
          aria-live="polite"
        >
          <p>{t("reading_file", { file: fileName })}</p>
          <div
            className="mmt-imp-progress"
            role="progressbar"
            aria-label={t("reading_file", { file: fileName })}
          >
            <span />
          </div>
          <p className="mmt-imp-reading-help">{t("reading_help")}</p>
        </div>
      ) : (
        <div className="mmt-imp-entry" data-imp-stage="entry">
          <input
            ref={inputRef}
            id="mmt-imp-file"
            type="file"
            accept={ACCEPTED_FILE_TYPES}
            hidden
            onChange={onFileChosen}
          />
          <button
            type="button"
            className="mmt-imp-primary"
            data-action="imp-pick"
            onClick={() => inputRef.current && inputRef.current.click()}
          >
            {t("choose_file")}
          </button>
          <p className="mmt-imp-formats">{t("supported_formats")}</p>
          <a
            className="mmt-imp-sample"
            download={sampleDoc.file}
            href={`data:${SAMPLE_PDF_MIME};base64,${sampleDoc.base64}`}
          >
            {t("download_sample", { person: personLabels.singular })}
          </a>
        </div>
      )}
    </div>
  );
}
